//-------------CORE-------------//
import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useSelector, useDispatch } from "react-redux";
import { unstable_batchedUpdates } from "react-dom";

//-------------MUI-------------//
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Card from "@mui/material/Card";
import DeviceHubSharpIcon from "@mui/icons-material/DeviceHubSharp";
import GetAppIcon from "@mui/icons-material/GetApp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
//----------EXTERNAL COMPS--------//
import { resetFilter, setFilter } from "rtkSlices/filterDevicesSlice";
import DeleteAlert from "components/Alerts/Delete";
import Table from "components/Table/newTable";
import Loader from "components/Progress";
import AddDevices from "./addDevices";
import devicesApi, {
  useGetDevicesQuery,
  useDeleteDevicesMutation,
} from "services/devices";
import { AdminToolbar, AdminSelect, AdminActions, RowAction, adminDateTime, adminPageSizes } from "components/Admin/adminTable";
import { statusFilters, statusOf } from "./deviceStatus";
import ImportDevices from "./importDevices";
import MoveDevices from "./moveDevices";

let timeout = null;
let temp = "";
const pageSizeOptions = adminPageSizes;
// the search looks through every identifier the table shows (the "Search by" picker can narrow
// it); status has its own filter, since the stored values (AVAILABLE...) are not the words shown
const searchFieldOptions = [
  { label: "Name", value: "name" },
  { label: "Serial Number", value: "serialNo" },
  { label: "Firmware", value: "firmware" },
  { label: "IMEI", value: "imei" },
  { label: "IMSI", value: "imsi" },
  { label: "ICCID", value: "iccid" },
  { label: "MSISDN", value: "msisdn" },
  { label: "Device ID", value: "internalId" },
];
const searchableFields = searchFieldOptions.map((f) => f.value);
// only what the table and the Edit dialog read (a full device record is ~9 KB, mostly control
// history; associatedGroups is kept for backends that still apply the group scope after the read)
const selectFields = {
  name: 1,
  serviceId: 1,
  internalId: 1,
  updatedAt: 1,
  measurementUpdateTime: 1,
  platformDeviceType: 1,
  location: 1,
  associatedGroups: 1,
  "packetFromPlatform.c8y_Hardware": 1,
  "packetFromPlatform.c8y_Firmware": 1,
  "packetFromPlatform.c8y_Mobile": 1,
  "packetFromPlatform.c8y_Availability": 1,
};
// the device model's default measurementUpdateTime: no measurement has arrived yet
const noMeasurementBefore = new Date("2001-06-16");
// the CSV export's columns, in the table's order; dates in ISO 8601 (UTC) so any locale reads them
const csvColumns = (getServiceName) => [
  ["Name", (d) => d.name],
  ["Status", (d) => statusOf(d.packetFromPlatform?.c8y_Availability?.status).label],
  ["Solution", (d) => getServiceName(d.serviceId)],
  ["ID", (d) => d.internalId],
  ["Serial Number", (d) => d.packetFromPlatform?.c8y_Hardware?.serialNumber],
  ["Firmware", (d) => d.packetFromPlatform?.c8y_Firmware?.version],
  ["IMEI", (d) => d.packetFromPlatform?.c8y_Mobile?.imei],
  ["IMSI", (d) => d.packetFromPlatform?.c8y_Mobile?.imsi],
  ["ICCID", (d) => d.packetFromPlatform?.c8y_Mobile?.iccid],
  ["MSISDN", (d) => d.packetFromPlatform?.c8y_Mobile?.msisdn],
  [
    "Last Message",
    (d) =>
      d.measurementUpdateTime &&
      new Date(d.measurementUpdateTime) > noMeasurementBefore
        ? new Date(d.measurementUpdateTime).toISOString()
        : "",
  ],
  ["Last Updated", (d) => (d.updatedAt ? new Date(d.updatedAt).toISOString() : "")],
];
// one CSV cell: quoted when it holds a comma, quote or line break; a value starting with = + - @
// (or a tab or carriage return) gets a leading ' so a spreadsheet never runs it as a formula
const csvCell = (value) => {
  let text = value == null ? "" : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = "'" + text;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export default function DM(props) {
  let keys = [
    { name: "name", key: "name" },
    { name: "imei", key: "packetFromPlatform.c8y_Mobile.imei" },
    { name: "imsi", key: "packetFromPlatform.c8y_Mobile.imsi" },
    { name: "iccid", key: "packetFromPlatform.c8y_Mobile.iccid" },
    { name: "msisdn", key: "packetFromPlatform.c8y_Mobile.msisdn" },
    {
      name: "serialNumber",
      key: "packetFromPlatform.c8y_Hardware.serialNumber",
    },
    {
      name: "firmware",
      key: "packetFromPlatform.c8y_Firmware.version",
    },
    { name: "updateTime", key: "updatedAt" },
    { name: "lastMessage", key: "measurementUpdateTime" },
    { name: "html2", key: "packetFromPlatform.c8y_Availability.status" },
  ];
  let token = window.localStorage.getItem("token");
  const dispatch = useDispatch();
  const [sort, setSortState] = useState({
    name: 1,
    html2: 0,
    firmware: 0,
    serialNumber: 0,
    imei: 0,
    imsi: 0,
    iccid: 0,
    msisdn: 0,
    lastMessage: 0,
    updateTime: 0,
  });
  const metaDataValue = useSelector((state) => state.metaData);
  // the solutions picked in the filter; none picked = all solutions
  const [selectedServices, setSelectedServices] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchFields, setSearchFields] = useState(searchableFields);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [deleteDevice, deleteResult] = useDeleteDevicesMutation();
  const [search, setSearch] = React.useState("");
  const [tempSearch, setTempSearch] = useState("");
  const groupIds = []
  metaDataValue?.services.forEach(s=>{
    if(s.group?.id){
      groupIds.push(`${s.id}:${s.group?.id}`)
    }
  })
  // one column sorts at a time: the clicked one keeps its new direction, the others reset
  const setSort = (update) => {
    setPage(1);
    setSortState((prev) => {
      const next = typeof update == "function" ? update(prev) : update;
      const clicked = Object.keys(next).find((k) => next[k] !== prev[k]);
      if (!clicked) return prev;
      const reset = {};
      Object.keys(prev).forEach((k) => (reset[k] = 0));
      return { ...reset, [clicked]: next[clicked] };
    });
  };
  const sortedKey = keys.find((k) => sort[k.name]);
  const sortedFields = sortedKey
    ? { [sortedKey.key]: sort[sortedKey.name] }
    : { name: 1 };
  // the list request for one page under the current filters (the table and the CSV export ask
  // the same question)
  function listArgs(currentPage, size) {
    return {
      token,
      group: JSON.stringify(
        selectedServices.length
          ? selectedServices
          : metaDataValue?.services.map((e) => e.id)
      ),
      params: `&pageSize=${size}&currentPage=${currentPage}&withTotalPages=true&sort=${JSON.stringify(
        sortedFields
      )}&select=${JSON.stringify(selectFields)}&search=${encodeURIComponent(
        search
      )}&searchFields=${JSON.stringify(searchFields)}${
        statusFilter ? `&connected=${statusFilter}` : ``
      }${
        groupIds.length ? `&associatedGroup=${JSON.stringify(groupIds)}` : ``
      }`,
    };
  }
  const devices = useGetDevicesQuery(listArgs(page, pageSize));

  const [activeId, setActiveId] = useState(null);
  const [deleteModal, setDelete] = useState(false);
  const [deviceEdit, setDeviceEdit] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [openPopup2, setOpenPopup2] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const metaData = useSelector((state) => state.metaData?.services);
  const { enqueueSnackbar } = useSnackbar();

  function getServiceName(id) {
    let output;
    metaData.forEach((elm) => {
      if (elm.id == id) {
        output = elm.name;
      }
    });
    return output;
  }
  function ifLoaded(state, component) {
    if (state) return <Loader top="20vh" />;
    else return component();
  }

  const handleSearch = (text) => {
    setTempSearch(text);
    temp = text;
    clearTimeout(timeout);
    timeout = setTimeout(function () {
      // one render (React 17 does not batch inside a timer), so one request
      unstable_batchedUpdates(() => {
        setPage(1);
        setSearch(temp.trim());
      });
    }, 1000);
  };

  // the table shows the page the server returned for THIS request (25 devices, already sorted and
  // filtered); a failed request shows no rows rather than the previous page's
  const rows = React.useMemo(
    () => (devices.currentData?.payload?.data || []).map(formatDevice),
    [devices.currentData, metaData]
  );
  const totalPages = devices.currentData?.payload?.totalPages || 0;

  useEffect(() => {
    if (devices.isError) {
      showSnackbar("Devices", devices.error?.data?.message, "error", 1000);
    }
  }, [devices.isError]);

  // a delete can empty the last page: step back to the new last page
  useEffect(() => {
    if (totalPages && page > totalPages) setPage(totalPages);
  }, [totalPages]);

  function handleUpdateDevice() {
    devices.refetch();
  }

  // CSV export: every device matching the current filters (not just this page), 500 per request
  const [exporting, setExporting] = useState(false);
  async function exportCsv() {
    setExporting(true);
    try {
      const all = [];
      for (let p = 1, pages = 1; p <= pages; p++) {
        // a one-off read: subscribe, take the result, let go of it
        const request = dispatch(
          devicesApi.endpoints.getDevices.initiate(listArgs(p, 500), {
            forceRefetch: true,
          })
        );
        let result;
        try {
          result = await request.unwrap();
        } finally {
          request.unsubscribe();
        }
        all.push(...(result?.payload?.data || []));
        pages = result?.payload?.totalPages || 0;
      }
      const columns = csvColumns(getServiceName);
      const lines = [
        columns.map(([header]) => header),
        ...all.map((d) => columns.map(([, value]) => value(d))),
      ].map((cells) => cells.map(csvCell).join(","));
      // the byte-order mark lets Excel read names like "Kraków" as UTF-8
      const blob = new Blob(["﻿" + lines.join("\r\n")], {
        type: "text/csv;charset=utf-8",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Devices ${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (error) {
      console.error("Device CSV export failed", error);
      showSnackbar("Devices", error?.data?.message || "Export failed", "error", 1000);
    } finally {
      setExporting(false);
    }
  }

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar({ title, message, variant }, { timeOut });
  }

  async function toggleDelete(id = null) {
    setActiveId(id);
    setDelete((state) => !state);
  }

  async function onDelete(e, check) {
    let deletedDevice = await deleteDevice({
      token,
      id: e,
      platformCheck: check,
    });
    if (deletedDevice.error) {
      showSnackbar("Device", deletedDevice.error?.data?.message, "error", 1000);
    } else {
      showSnackbar("Device", deletedDevice.data?.message, "success", 1000);
      toggleDelete();
    }
  }

  const handlepopupToggle = () => {
    setOpenPopup(!openPopup);
    setDeviceEdit("");
  };

  const handlepopupToggle2 = () => {
    setOpenPopup2(!openPopup2);
  };

  // D29 (b): adding devices by hand is removed (a hand-added device has no external
  // identity, so real hardware can never attach to it). Import brings in devices that
  // registered themselves; the row Edit dialog (openPopup2) stays.
  const actions = [
    {
      icon: <GetAppIcon />,
      name: "Import Devices",
      onClick: handlepopupToggle,
    },
    {
      icon: <SwapHorizIcon />,
      name: "Move Devices",
      onClick: () => setMoveOpen(true),
    },
  ];

  function columns() {
    let temp = [
      { id: "name", label: "Name", align: "center" },
      { id: "html2", label: "Status", align: "center" },
      { id: "dashboard", label: "Solution", align: "center" },
      { id: "id", label: "ID", align: "center" },
      { id: "serialNumber", label: "Serial Number", align: "center" },
      { id: "firmware", label: "Firmware", align: "center" },
      { id: "imei", label: "IMEI", align: "center" },
      { id: "imsi", label: "IMSI", align: "center" },
      { id: "iccid", label: "ICCID", align: "center" },
      { id: "msisdn", label: "MSISDN", align: "center" },
      { id: "lastMessage", label: "Last Message", align: "center" },
      {
        id: "updateTime",
        label: "Last Updated",
        align: "center",
      },
    ];
    if (props.permission == "ALL")
      temp.push({
        id: "html",
        label: "Actions",
        minWidth: 150,
        align: "center",
        disableSorting: true,
      });
    return temp;
  }

  function formatDevice(elm) {
    const measured =
      elm.measurementUpdateTime &&
      new Date(elm.measurementUpdateTime) > noMeasurementBefore;
    return {
      name: elm.name,
      dashboard: getServiceName(elm.serviceId),
      id: elm.internalId,
      updateTime: adminDateTime(elm.updatedAt),
      // formatted by the table (its "Last Message" date format)
      lastMessage: measured ? elm.measurementUpdateTime : "-",
      status: elm.packetFromPlatform?.c8y_Availability?.status,
      serialNumber: elm.packetFromPlatform?.c8y_Hardware?.serialNumber,
      firmware: elm.packetFromPlatform?.c8y_Firmware?.version,
      imei: elm.packetFromPlatform?.c8y_Mobile?.imei,
      imsi: elm.packetFromPlatform?.c8y_Mobile?.imsi,
      iccid: elm.packetFromPlatform?.c8y_Mobile?.iccid,
      msisdn: elm.packetFromPlatform?.c8y_Mobile?.msisdn,
      html: elm.internalId,
      platformDeviceType: elm.platformDeviceType,
      location: elm.location,
    };
  }

  var html2 = (row) => {
    const status = statusOf(row.status);
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          whiteSpace: "nowrap",
          color: status.color,
          fontSize: "13px",
        }}
      >
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: status.color,
          }}
        />
        {status.label}
      </span>
    );
  };

  var html = (row) => {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <RowAction
          title="Edit"
          icon={<EditIcon />}
          onClick={() => {
            setDeviceEdit(row);
            setOpenPopup2(true);
          }}
        />
        <RowAction id={row.html} title="Delete" icon={<DeleteIcon />} onClick={() => toggleDelete(row.html)} />
      </div>
    );
  };

  const pageChange = (newPage) => {
      setPage(newPage);
    }

  function cardFunc() {
    return (
      <Table
        columns={columns()}
        rows={rows}
        html={html}
        html2={html2}
        page={page}
        totalPages={totalPages}
        totalDocuments={devices.currentData?.payload?.totalDocuments || 0}
        handleChange={pageChange}
        height="calc(100vh - 280px)"
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        setPageSize={setPageSize}
        setSort={setSort}
        sort={sort}
        sortedFields={sortedFields}
        keys={keys}
        minWidth="100%"
        overflowX="auto"
        // the page-number row is as wide as the table area; side margins would make the area scroll
        // sideways a second time, under the table's own scrollbar
        paginationMargin="2vh 0"
      />
    );
  }

  // the "Search by" picker: a new choice sends the table back to page 1
  const pickSearchFields = (picked) => {
    setPage(1);
    setSearchFields(picked);
  };

  return (
    <div>
      {props.permission == "ALL" ? <AdminActions actions={actions} /> : null}
      {openPopup ? (
        <ImportDevices open={openPopup} onClose={handlepopupToggle} />
      ) : null}
      {moveOpen ? <MoveDevices open={moveOpen} onClose={() => setMoveOpen(false)} /> : null}
      {openPopup2 ? (
        <AddDevices
          deviceEdit={deviceEdit}
          openPopup2={openPopup2}
          handlepopupToggle2={handlepopupToggle2}
          handleUpdateDevice={handleUpdateDevice}
        />
      ) : null}
      {/* The admin page area grows to fit its widest content, so without a cap a wide table made
          the whole page wider than the screen and opening the solution filter scrolled the page
          sideways. Capped to the screen (minus the sidebar and page padding), the table scrolls
          inside the card instead. */}
      <Card style={{ maxWidth: "calc(100vw - 90px)" }}>
        <AdminToolbar
          title="Devices"
          icon={<DeviceHubSharpIcon color="disabled" />}
          filters={
            <>
              <AdminSelect
                id="demo-select-small"
                label="Filter By Solution"
                width="20%"
                multiple
                allLabel="All solutions"
                value={selectedServices}
                onChange={(picked) => {
                  setPage(1);
                  setSelectedServices(picked);
                }}
                options={metaData.map((elm) => ({ value: elm.id, label: elm.name }))}
                renderValue={(picked) =>
                  !picked.length
                    ? "All solutions"
                    : picked.length == 1
                    ? getServiceName(picked[0])
                    : `${picked.length} solutions`
                }
              />
              <AdminSelect
                id="device-status-filter"
                label="Filter By Status"
                width="12%"
                value={statusFilter}
                onChange={(value) => {
                  setPage(1);
                  setStatusFilter(value);
                }}
                options={statusFilters}
              />
            </>
          }
          search={{
            value: tempSearch,
            onChange: handleSearch,
            placeholder: "Search name, serial, firmware, IMEI, IMSI, ICCID, MSISDN, ID ...",
            fields: searchFieldOptions,
            picked: searchFields,
            onPick: pickSearchFields,
          }}
          extra={
            <Tooltip title="Export to CSV" placement="top" arrow>
              <span style={{ marginRight: "10px" }}>
                <IconButton
                  id="export-devices"
                  onClick={exportCsv}
                  disabled={exporting || !rows.length}
                  style={{ width: "30px", height: "30px" }}
                >
                  {exporting ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DownloadIcon
                      style={{ width: "25px", height: "25px", color: "#333" }}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          }
        />
        <div
          style={{
            margin: "20px",
            minHeight: "50vh",
          }}
        >
          {ifLoaded(devices.isFetching, cardFunc)}
        </div>
      </Card>

      {activeId ? (
        <DeleteAlert
          deleteModal={deleteModal}
          title={`Delete (${(rows.find((r) => r.html == activeId) || {}).name || "device"})`}
          adminStyle
          question="Are you sure you want to delete this device?"
          platformCheck={true}
          id={activeId}
          handleDelete={onDelete}
          handleClose={toggleDelete}
          deleteResult={deleteResult}
        />
      ) : null}
    </div>
  );
}
